import React, { Fragment, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Drawer } from "components/Drawer";
import { PageHeader } from "components/PageHeader";
import SimpleToast from "components/SimpleToast";

import { clearStates, handleAddButton } from "./constants";
import { IContainerCrud } from "./interface";
import { toastOption } from "../../../global/types";
import { modelForm } from "../../forms/optionsForms";
import { ManagementListsAdmin } from "../ManagementListsAdmin";

export const ContainerCrud: React.FC<IContainerCrud> = ({
  list,
  permissions,
  title,
  emptyListMessage,
  optionForm,
  customProsForm,
  modelFormBase,
  loading,
  isAudience,
  SkeletonComponent,
  serverSidePagination = false,
  totalCount,
  currentPage = 0,
  serverPageCount = 0,
  onPageChange,
  onSearchChange,
  onSituationChange,
  onAfterSubmit,
}) => {
  const { t } = useTranslation();
  const [listState, setListState] = useState<Array<any>>([]);

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [clickToEdit, setClickToEdit] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(list && list.length);
  const [updateList, setUpdateList] = useState<number>(0);

  //Toast
  const [toastTrigger, setToastTrigger] = useState<number>(0);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastType, setToastType] = useState<toastOption>("warning");

  useEffect(() => {
    if (
      modelFormBase.objectRetorned !== null &&
      modelFormBase.onSubmitForm > 0
    ) {
      if (serverSidePagination && onAfterSubmit) {
        configureToast(
          `${title} ${isEdit ? t("common.updatedSuccessfully") : t("common.createdSuccessfully")}`,
          "success",
        );
        setOpenDrawer(false);
        setIsEdit(false);
        modelFormBase.setOnSubmitForm(0);
        modelFormBase.setObjectRetorned(null);
        onAfterSubmit();
      } else {
        const listToAddorUpdate = listState;
        if (!isEdit) {
          listToAddorUpdate.push(modelFormBase.objectRetorned);
          setListState(listToAddorUpdate);
          setUpdateList(updateList + 1);
          configureToast(
            `${title} ${t("common.createdSuccessfully")}`,
            "success",
          );
        } else {
          const index = listToAddorUpdate.findIndex(
            (obj) => obj?.id === modelFormBase.objectRetorned?.id,
          );
          if (index !== -1) {
            listToAddorUpdate[index] = modelFormBase.objectRetorned;
            setListState(listToAddorUpdate);
            setUpdateList(updateList + 1);
            configureToast(
              `${title} ${t("common.updatedSuccessfully")}`,
              "success",
            );
          } else {
            configureToast(t("common.unexpectedError"), "error");
          }
        }
        modelFormBase.list = listState;

        setOpenDrawer(false);
        setIsEdit(false);
        modelFormBase.setOnSubmitForm(0);
        modelFormBase.setObjectRetorned(null);
      }
    }
  }, [modelFormBase.objectRetorned]);

  useEffect(() => {
    if (modelFormBase.objectRetorned !== null) {
      modelFormBase.setOnSubmitForm(0);
      setIsEdit(true);
      setOpenDrawer(true);

      customProsForm.modelFormBase.setHttpType("put");
    }
    modelFormBase.setOnSubmitForm(0);
  }, [clickToEdit]);

  useEffect(() => {
    setListState(Array.isArray(list) ? list : []);
  }, [list]);

  const handleOpenDrawerAdd = () => {
    handleAddButton(
      setOpenDrawer,
      setIsEdit,
      customProsForm.modelFormBase.setHttpType,
    );
    modelFormBase.setObjectRetorned(null);
    modelFormBase.setOnSubmitForm(0);
  };

  const handleCloseDrawer = () => {
    clearStates(setOpenDrawer, setIsEdit, modelFormBase.setObjectRetorned);
    modelFormBase.setOnSubmitForm(0);
  };

  const handleApplyButton = async () => {
    modelFormBase.setOnSubmitForm(modelFormBase.onSubmitForm + 1);
  };

  const configureToast = (
    toastBodyMessage: string,
    toastOption: toastOption,
  ) => {
    setToastBody(toastBodyMessage);
    setToastType(toastOption);
    setToastTrigger(toastTrigger + 1);
  };

  return (
    <>
      <SimpleToast
        id="attendantRoleToastId"
        title={title}
        type={toastType}
        description={toastBody}
        handleCloseToast={() => {}}
        trigger={toastTrigger}
      />

      <Fragment>
        <PageHeader
          quantity={permissions.toRead ? quantity : 0}
          id="headerAdminArea"
          label={title}
          background="secondary"
          addClick={
            permissions.toCreate ? () => handleOpenDrawerAdd() : undefined
          }
        />
        <ManagementListsAdmin
          emptyMessage={emptyListMessage}
          list={listState}
          permitions={permissions}
          setObjectRetorned={modelFormBase.setObjectRetorned}
          setClickToEdit={setClickToEdit}
          setQuantity={setQuantity}
          updateList={updateList}
          clickToEdit={clickToEdit}
          titleToast={title}
          loading={loading}
          isAudience={isAudience}
          SkeletonComponent={SkeletonComponent}
          serverSidePagination={serverSidePagination}
          totalCount={totalCount}
          currentPage={currentPage}
          serverPageCount={serverPageCount}
          onPageChange={onPageChange}
          onSearchChange={onSearchChange}
          onSituationChange={onSituationChange}
        />
        <Drawer
          title={
            isEdit
              ? `${t("common.edit")} ${title.toLowerCase()}`
              : `${t("common.register")} ${title.toLowerCase()}`
          }
          open={openDrawer}
          onClose={handleCloseDrawer}
          onApply={handleApplyButton}
          paddingLeft={33}
          paddingRight={33}
        >
          {modelForm(optionForm, modelFormBase, customProsForm)}
        </Drawer>
      </Fragment>
    </>
  );
};
